/**
 * @packageDocumentation
 * @module Auth
 * @category Core
 */

import { inject, Injectable } from '@angular/core';
import { AuthTokenStateActions } from '@app/core/auth/states/auth-token.state.actions';
import { ProfileModel } from '@app/core/graphql-global-types';
import { UpdateSidenavItemAction } from '@app/core/sidenav/store/sidenav.actions';
import { Store } from '@ngxs/store';
import { BehaviorSubject, filter, Observable, switchMap, tap } from 'rxjs';

import { AuthProfileState } from '../states/auth-profile.state';
import { AuthProfileStateActions } from '../states/auth-profile.state.actions';
import { AuthTokenState } from '../states/auth-token.state';

/**
 * Сервис для работы с авторизацией.
 *
 * @group Services
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Профиль текущего пользователя */
  profile$: Observable<ProfileModel | null>;

  /**
   * Признак аутентификации.
   * Пользователь считается аутентифицированным, если сохранен токен.
   */
  authenticated$: Observable<boolean>;

  protected profileLoadedSubject = new BehaviorSubject(false);

  protected initialized = false;

  private readonly store = inject(Store);

  constructor() {
    this.profile$ = this.profileLoadedSubject.pipe(
      filter((loaded) => loaded),
      switchMap(() => this.store.select(AuthProfileState.profile)),
    );

    this.authenticated$ = this.profileLoadedSubject.pipe(
      filter((loaded) => loaded),
      switchMap(() => this.store.select(AuthTokenState.authenticated)),
    );
  }

  /** Access токен текущего пользователя */
  public get accessToken(): string | null {
    return this.store.selectSnapshot(AuthTokenState.accessToken);
  }

  /**
   * Инициализация авторизации.
   * Отслеживает изменение токена и перезапрашивает профиль пользователя.
   */
  public init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    this.store
      .select(AuthTokenState.accessToken)
      .pipe(
        tap(() => this.profileLoadedSubject.next(false)),
        switchMap(() => this.store.dispatch(new AuthProfileStateActions.Load())),
        switchMap(() => this.store.dispatch(new AuthProfileStateActions.InitPermissions())),
        switchMap(() => this.store.dispatch(new UpdateSidenavItemAction())),
        tap(() => this.profileLoadedSubject.next(true)),
      )
      .subscribe();
  }

  /** Выход из системы */
  public logout(): void {
    this.store.dispatch(new AuthTokenStateActions.Logout());
  }
}
